import React, { useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectModal = ({ project, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e?.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !project) return null;

  const getTechBadgeColor = (tech) => {
    const colors = {
      'React': 'bg-blue-100 text-blue-800',
      'Node.js': 'bg-green-100 text-green-800',
      'MongoDB': 'bg-green-100 text-green-800',
      'Express': 'bg-gray-100 text-gray-800',
      'TypeScript': 'bg-blue-100 text-blue-800',
      'Next.js': 'bg-black text-white',
      'Tailwind': 'bg-cyan-100 text-cyan-800',
      'Firebase': 'bg-orange-100 text-orange-800',
      'PostgreSQL': 'bg-blue-100 text-blue-800',
      'Python': 'bg-yellow-100 text-yellow-800',
      'Django': 'bg-green-100 text-green-800',
      'Vue.js': 'bg-green-100 text-green-800',
      'AWS': 'bg-orange-100 text-orange-800',
      'Docker': 'bg-blue-100 text-blue-800',
      'GraphQL': 'bg-pink-100 text-pink-800'
    };
    return colors?.[tech] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal Content */}
      <div className="relative bg-card rounded-xl shadow-overlay max-w-4xl w-full max-h-[90vh] overflow-y-auto animate-fade-in">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground">{project?.title}</h2>
            <p className="text-muted-foreground mt-1">{project?.type} • {project?.year}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Project Image Gallery */}
          <div className="mb-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Image
                  src={project?.image}
                  alt={project?.title}
                  className="w-full h-64 md:h-80 object-cover rounded-lg"
                />
              </div>
              {project?.screenshots && project?.screenshots?.map((screenshot, index) => (
                <Image
                  key={index}
                  src={screenshot}
                  alt={`${project?.title} screenshot ${index + 1}`}
                  className="w-full h-48 object-cover rounded-lg"
                />
              ))}
            </div>
          </div>

          {/* Project Details Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Overview */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Project Overview</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {project?.fullDescription}
                </p>
              </div>

              {/* Problem Statement */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Problem Statement</h3>
                <p className="text-muted-foreground leading-relaxed">
                  {project?.problemStatement}
                </p>
              </div>

              {/* Solution */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Technical Solution</h3>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  {project?.solution}
                </p>
                {project?.keyFeatures && (
                  <ul className="space-y-2">
                    {project?.keyFeatures?.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Icon name="Check" size={16} className="text-success mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Challenges */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Challenges & Solutions</h3>
                <div className="space-y-3">
                  {project?.challenges && project?.challenges?.map((challenge, index) => (
                    <div key={index} className="bg-muted/50 rounded-lg p-4">
                      <h4 className="font-medium text-foreground mb-2">{challenge?.title}</h4>
                      <p className="text-muted-foreground text-sm">{challenge?.solution}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Project Info */}
              <div className="bg-muted/30 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-foreground mb-4">Project Info</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Duration</span>
                    <span className="text-foreground font-medium">{project?.duration}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Team Size</span>
                    <span className="text-foreground font-medium">{project?.teamSize}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">My Role</span>
                    <span className="text-foreground font-medium">{project?.role}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      project?.status === 'Completed' ? 'bg-success/20 text-success' :
                      project?.status === 'In Progress'? 'bg-warning/20 text-warning' : 'bg-muted text-muted-foreground'
                    }`}>
                      {project?.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Technologies */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">Technologies Used</h3>
                <div className="flex flex-wrap gap-2">
                  {project?.technologies?.map((tech, index) => (
                    <span
                      key={index}
                      className={`px-3 py-1 text-sm font-medium rounded-md ${getTechBadgeColor(tech)}`}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {project?.liveUrl && (
                  <Button
                    variant="default"
                    fullWidth
                    iconName="ExternalLink"
                    iconPosition="left"
                    onClick={() => window.open(project?.liveUrl, '_blank')}
                  >
                    View Live Demo
                  </Button>
                )}
                {project?.githubUrl && (
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Github"
                    iconPosition="left"
                    onClick={() => window.open(project?.githubUrl, '_blank')}
                  >
                    View Source Code
                  </Button>
                )}
              </div>

              {/* Project Stats */}
              <div className="bg-muted/30 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-foreground mb-3">Project Impact</h3>
                <div className="space-y-3">
                  {project?.metrics && project?.metrics?.map((metric, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-muted-foreground">{metric?.label}</span>
                      <span className="text-foreground font-medium">{metric?.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectModal;